/**
 * Notifications on this device (docs/notifications/contract.md, the v2 build; docs/NESTS.md,
 * "Stage 3: notifications"): what it asked for, the one state the bell shows, and the
 * moments' memory. One versioned blob, `arrow:v1:push` (`v: 2` inside; a `v: 1` blob is
 * carried over), parsed defensively; the rules are pure functions of it, the date and the
 * moment, so they're tested without React.
 *
 * The api holds the subscription and is the truth for what's sent; this keeps a copy of
 * the choices so the sheet can show them at once (and offline), and remembers the moments:
 * at most one a day, never before the first solve, and "Not now" rests them all 7 days,
 * then 30, then for good (one shared memory).
 */
import { addDays, type DateKey, daysBetween, isDateKey, type PlayerStats } from '@arrow/game-engine';

import type { KeyValueStore } from '../storage/kv.ts';

export const PUSH_KEY = 'arrow:v1:push';

// ------------------------------------------------------------------------------ the prefs

/** `"HH:MM"`, on a whole half hour. */
export type HalfHour = string;

export interface PushPrefs {
	/** From `morningAt`, when today hasn't been played. Off by default. */
	morning: boolean;
	morningAt: HalfHour;
	/** From `streakSaverAt`, when yesterday was played and today hasn't been. Off by default. */
	streakSaver: boolean;
	streakSaverAt: HalfHour;
	/** 19:30, on days someone in a nest played. */
	roundUps: boolean;
	nudges: boolean;
	/** Sunday at 20:00: the week's feathers so far. */
	sundayTable: boolean;
	/** Someone in a nest beat your time on a level you'd solved. Off by default. */
	beatTime: boolean;
	/** Quiet hours for the nest kinds; equal = none. May wrap past midnight. */
	quietFrom: HalfHour;
	quietTo: HalfHour;
}

export type SwitchKey = 'morning' | 'streakSaver' | 'roundUps' | 'nudges' | 'sundayTable' | 'beatTime';
export type TimeKey = 'morningAt' | 'streakSaverAt' | 'quietFrom' | 'quietTo';
export type PrefKey = keyof PushPrefs;
export const SWITCH_KEYS: readonly SwitchKey[] = ['morning', 'streakSaver', 'roundUps', 'nudges', 'sundayTable', 'beatTime'];
export const TIME_KEYS: readonly TimeKey[] = ['morningAt', 'streakSaverAt', 'quietFrom', 'quietTo'];
/** The nest's own kinds: the sheet's "Your nests" switch turns these on (beat your time stays a choice in the full list). */
export const NEST_KEYS: readonly SwitchKey[] = ['roundUps', 'nudges', 'sundayTable'];

/** What a new subscription starts with on the api (the contract's defaults). */
export const DEFAULT_PREFS: Readonly<PushPrefs> = Object.freeze({
	morning: false,
	morningAt: '07:00',
	streakSaver: false,
	streakSaverAt: '20:00',
	roundUps: true,
	nudges: true,
	sundayTable: true,
	beatTime: false,
	quietFrom: '21:30',
	quietTo: '08:00',
});
/** Off: every switch off, the times at their defaults. */
export const NO_PREFS: Readonly<PushPrefs> = Object.freeze({
	...DEFAULT_PREFS,
	roundUps: false,
	nudges: false,
	sundayTable: false,
});

const toMinutes = (t: string): number | null => {
	const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(t);
	return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const fromMinutes = (n: number): HalfHour => `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;

/** Every half hour from `from` to `to` (inclusive), as `"HH:MM"`. */
export function halfHours(from: HalfHour, to: HalfHour): HalfHour[] {
	const a = toMinutes(from) ?? 0;
	const b = toMinutes(to) ?? 0;
	const out: HalfHour[] = [];
	for (let n = a; n <= b; n += 30) out.push(fromMinutes(n));
	return out;
}

/** The choices each time offers (the contract's ranges). */
export const TIME_CHOICES: Readonly<Record<TimeKey, readonly HalfHour[]>> = Object.freeze({
	morningAt: halfHours('05:00', '11:30'),
	streakSaverAt: halfHours('16:00', '23:00'),
	quietFrom: halfHours('00:00', '23:30'),
	quietTo: halfHours('00:00', '23:30'),
});

export const isTimeFor = (key: TimeKey, value: unknown): value is HalfHour => typeof value === 'string' && TIME_CHOICES[key].includes(value);

/** Reads prefs from anything (the api's answer, a stored blob): bad fields fall back to `base`. */
export function readPrefs(raw: unknown, base: Readonly<PushPrefs>): PushPrefs {
	const p = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
	const out: PushPrefs = { ...base };
	for (const k of SWITCH_KEYS) if (typeof p[k] === 'boolean') out[k] = p[k];
	for (const k of TIME_KEYS) if (isTimeFor(k, p[k])) out[k] = p[k];
	return out;
}

export const anySwitchOn = (prefs: PushPrefs): boolean => SWITCH_KEYS.some((k) => prefs[k]);

// -------------------------------------------------------------------------- the moments

export type MomentKind = 'first_solve' | 'streak_solve' | 'nest_join' | 'nudge_sent';

export interface MomentMemory {
	/** "Not now"s so far (any moment). */
	dismissals: number;
	/** Rests until this day (exclusive). */
	restUntil: DateKey | null;
	never: boolean;
	/** The day a moment last showed, and which: at most one a day. */
	shownOn: DateKey | null;
	shownKind: MomentKind | null;
	/** A nest just joined or started, waiting for its moment (a week at most). */
	nestJoin: { name: string; on: DateKey } | null;
}

export interface PushState {
	v: 2;
	/** This device has a subscription on the api (as far as it last heard). */
	on: boolean;
	prefs: PushPrefs;
	/** The subscription vanished while permission was still granted (a phone update, a reinstall). */
	stopped: boolean;
	/** The zone last sent, and the local day it was last synced. */
	zone: string | null;
	syncedOn: DateKey | null;
	moments: MomentMemory;
	/** The local day `notify_state` was last sent (once a day). */
	stateSentOn: DateKey | null;
	/** The play state last sent (`on:streak`), so a launch sends it only when it changed. */
	playedSent: string | null;
}

export const EMPTY_MOMENTS: Readonly<MomentMemory> = Object.freeze({ dismissals: 0, restUntil: null, never: false, shownOn: null, shownKind: null, nestJoin: null });
export const EMPTY_PUSH: PushState = {
	v: 2,
	on: false,
	prefs: { ...NO_PREFS },
	stopped: false,
	zone: null,
	syncedOn: null,
	moments: { ...EMPTY_MOMENTS },
	stateSentOn: null,
	playedSent: null,
};

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const MOMENT_KINDS: readonly MomentKind[] = ['first_solve', 'streak_solve', 'nest_join', 'nudge_sent'];
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

function parseMoments(raw: unknown): MomentMemory {
	const o = obj(raw);
	const join = obj(o.nestJoin);
	return {
		dismissals: isCount(o.dismissals) ? Math.min(o.dismissals, 9) : 0,
		restUntil: isDateKey(o.restUntil) ? o.restUntil : null,
		never: o.never === true,
		shownOn: isDateKey(o.shownOn) ? o.shownOn : null,
		shownKind: MOMENT_KINDS.includes(o.shownKind as MomentKind) ? (o.shownKind as MomentKind) : null,
		nestJoin:
			typeof join.name === 'string' && join.name.length > 0 && join.name.length <= 80 && isDateKey(join.on) ? { name: join.name, on: join.on } : null,
	};
}

/** v1 kept two asks (the nest's and the morning's): the moments carry on from the more-refused one. */
function momentsFromV1(asks: unknown): MomentMemory {
	const a = obj(asks);
	const [nest, morning] = [parseMoments(a.nest), parseMoments(a.morning)];
	const more = morning.never || morning.dismissals > nest.dismissals ? morning : nest;
	return { ...EMPTY_MOMENTS, dismissals: more.dismissals, restUntil: more.restUntil, never: nest.never || morning.never };
}

/** Parses a stored blob (v2, or v1 carried over). Anything unreadable reads as a fresh state; never throws. */
export function parsePushState(raw: string | null): PushState {
	if (!raw) return structuredClone(EMPTY_PUSH);
	try {
		const o = obj(JSON.parse(raw));
		if (o.v !== 1 && o.v !== 2) return structuredClone(EMPTY_PUSH);
		return {
			v: 2,
			on: o.on === true,
			prefs: readPrefs(o.prefs, NO_PREFS),
			stopped: o.stopped === true,
			zone: typeof o.zone === 'string' && o.zone.length <= 64 ? o.zone : null,
			syncedOn: isDateKey(o.syncedOn) ? o.syncedOn : null,
			moments: o.v === 1 ? momentsFromV1(o.asks) : parseMoments(o.moments),
			stateSentOn: isDateKey(o.stateSentOn) ? o.stateSentOn : null,
			playedSent: typeof o.playedSent === 'string' && o.playedSent.length <= 32 ? o.playedSent : null,
		};
	} catch {
		return structuredClone(EMPTY_PUSH);
	}
}

export function loadPushState(store: KeyValueStore): PushState {
	return parsePushState(store.get(PUSH_KEY));
}

export function savePushState(store: KeyValueStore, state: PushState): void {
	store.set(PUSH_KEY, JSON.stringify(state));
}

// ---------------------------------------------------------------------------- the state

/** Where this browser stands: it can subscribe, it needs the home screen first (an iPhone in Safari), or it can't. */
export type PushRoute = 'push' | 'install' | 'none';
export type Permission = 'default' | 'granted' | 'denied';

/** The one state the bell, the sheet and the moments read (contract, `notify_state`). */
export type NotifyState = 'unsupported' | 'needs_home' | 'off' | 'blocked' | 'on' | 'stopped';

export function notifyState(input: { route: PushRoute; permission: Permission; on: boolean; stopped: boolean }): NotifyState {
	if (input.route === 'none') return 'unsupported';
	if (input.route === 'install') return 'needs_home';
	if (input.permission === 'denied') return 'blocked';
	if (input.on) return input.stopped ? 'stopped' : 'on';
	return 'off';
}

/** What the bell looks like: a dot while there's something to turn on, filled once on, plain otherwise. */
export function bellLook(state: NotifyState): 'dot' | 'filled' | 'plain' | 'hidden' {
	if (state === 'unsupported') return 'hidden';
	if (state === 'off' || state === 'needs_home') return 'dot';
	return state === 'on' ? 'filled' : 'plain';
}

/** The bell's accessible name: "Notifications, off". */
export function bellLabel(state: NotifyState): string {
	const words: Record<NotifyState, string> = {
		unsupported: 'not available here',
		needs_home: 'off',
		off: 'off',
		blocked: 'blocked',
		on: 'on',
		stopped: 'stopped',
	};
	return `Notifications, ${words[state]}`;
}

// ---------------------------------------------------------------------- the rules

/** "Not now": 7 days, then 30, then never again. */
export const REST_DAYS = [7, 30] as const;

export function afterNotNow(m: MomentMemory, today: DateKey): MomentMemory {
	const dismissals = m.dismissals + 1;
	const days = REST_DAYS[dismissals - 1];
	return { ...m, dismissals, never: days === undefined, restUntil: days === undefined ? m.restUntil : addDays(today, days) };
}

export const isResting = (m: Pick<MomentMemory, 'never' | 'restUntil'>, today: DateKey): boolean =>
	m.never || (m.restUntil !== null && today < m.restUntil);

/** What a "Not now" says: when it'll ask again. */
export function notNowLine(next: MomentMemory): string {
	if (next.never) return 'Sparrow won’t ask again. Turn them on from the bell.';
	return next.dismissals <= 1 ? 'Sparrow will ask again next week.' : 'Sparrow will ask again next month.';
}

/** A nest's moment waits this long for a day it may show. */
export const NEST_JOIN_DAYS = 7;

export interface MomentContext {
	today: DateKey;
	state: NotifyState;
	/** A daily has been finished on this device. */
	solvedBefore: boolean;
}

/**
 * Whether `kind`'s moment may show now: while notifications are off (or need the home
 * screen), after the first solve, not while "Not now" rests them, and at most one moment
 * a day (the one already showing today may keep showing). The nest's needs a nest just
 * joined or started, within the week.
 */
export function momentVisible(m: MomentMemory, kind: MomentKind, ctx: MomentContext): boolean {
	// Nest's card also shows when they're blocked or stopped (its sheet says how to get them
	// back); the other moments only ask while they're plainly off.
	const asks = ctx.state === 'off' || ctx.state === 'needs_home' || (kind === 'nest_join' && (ctx.state === 'blocked' || ctx.state === 'stopped'));
	if (!asks) return false;
	if (!ctx.solvedBefore || isResting(m, ctx.today)) return false;
	if (m.shownOn === ctx.today && m.shownKind !== kind) return false;
	// Nest asks on any visit while they're off (James, 2 Nov), not only after a join.
	return true;
}

/** The nest waiting for its moment, if it's still within the week. */
export function pendingNest(m: MomentMemory, today: DateKey): string | null {
	if (!m.nestJoin) return null;
	const age = daysBetween(m.nestJoin.on, today);
	return age >= 0 && age < NEST_JOIN_DAYS ? m.nestJoin.name : null;
}

export function noteShown(m: MomentMemory, kind: MomentKind, today: DateKey): MomentMemory {
	return m.shownOn === today && m.shownKind === kind ? m : { ...m, shownOn: today, shownKind: kind };
}

/**
 * The result poster's moment: today's daily, finished on its day, never a challenge's
 * result. A streak of two or more asks to keep it going; otherwise tomorrow's reminder.
 */
export function resultMoment(input: {
	kind: string | undefined;
	dateKey: string | null;
	todayKey: string | undefined;
	challenge: boolean;
	streak: number;
}): 'first_solve' | 'streak_solve' | null {
	if (input.kind !== 'daily' || input.challenge || !input.dateKey || input.dateKey !== input.todayKey) return null;
	return input.streak >= 2 ? 'streak_solve' : 'first_solve';
}

// ------------------------------------------------------------------------- play state

/** What the api is told so a reminder never arrives after you've played (contract, "Play state"). */
export interface Played {
	on: DateKey;
	streak: number;
}

/** Any daily finished on this device (any day). */
export function hasSolved(stats: PlayerStats): boolean {
	return Object.values(stats.days).some((day) => Object.values(day ?? {}).some((r) => r?.completedAt != null));
}

/**
 * The last day a daily was finished on its own day (the streak's kind of day), and the
 * streak as of today; null before any. `streak` is the caller's (the engine's `currentStreak`).
 */
export function playedState(stats: PlayerStats, streak: number): Played | null {
	let last: DateKey | null = null;
	for (const [key, day] of Object.entries(stats.days)) {
		if (!isDateKey(key)) continue;
		const counted = Object.values(day ?? {}).some((r) => r?.completedAt != null && r.completedOnDay === key);
		if (counted && (last === null || key > last)) last = key;
	}
	return last ? { on: last, streak: Math.max(0, Math.min(10000, Math.floor(streak))) } : null;
}

export const playedKey = (p: Played | null): string | null => (p ? `${p.on}:${p.streak}` : null);

// ------------------------------------------------------------------------- the sheet

/** A group of switches in the sheet: the morning reminder, the streak saver, the nest's kinds. */
export type SheetGroup = 'morning' | 'streakSaver' | 'nests';

/** The sheet's switches in order, the moment's own first. */
export function groupOrder(first: SheetGroup | null, nests: boolean): SheetGroup[] {
	const all: SheetGroup[] = nests ? ['morning', 'streakSaver', 'nests'] : ['morning', 'streakSaver'];
	return first && all.includes(first) ? [first, ...all.filter((g) => g !== first)] : all;
}

/** The moment's own group, first in the sheet. */
export const MOMENT_GROUP: Record<MomentKind, SheetGroup> = {
	first_solve: 'morning',
	streak_solve: 'streakSaver',
	nest_join: 'nests',
	nudge_sent: 'nests',
};

/** The prefs a "Turn on notifications" sends, from the sheet's three switches. */
export function prefsFromSheet(groups: Record<SheetGroup, boolean>, times: Pick<PushPrefs, 'morningAt' | 'streakSaverAt'>): PushPrefs {
	return {
		...DEFAULT_PREFS,
		...times,
		morning: groups.morning,
		streakSaver: groups.streakSaver,
		roundUps: groups.nests,
		nudges: groups.nests,
		sundayTable: groups.nests,
	};
}

/** "Morning reminder at 07:00, the streak saver at 20:00, and your nests." */
export function setLine(prefs: PushPrefs, nests: boolean): string {
	const parts: string[] = [];
	if (prefs.morning) parts.push(`Morning reminder at ${prefs.morningAt}`);
	if (prefs.streakSaver) parts.push(`${parts.length ? 'the streak saver' : 'Streak saver'} at ${prefs.streakSaverAt}`);
	if (nests && NEST_KEYS.some((k) => prefs[k])) parts.push(parts.length ? 'your nests' : 'Your nests');
	if (parts.length === 0) return 'They’re on for this device.';
	if (parts.length === 1) return `${parts[0]}.`;
	return `${parts.slice(0, -1).join(', ')}${parts.length > 2 ? ',' : ''} and ${parts.at(-1)}.`;
}
